import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useBooleanFlagValue, useObjectFlagValue } from '@openfeature/react-sdk'
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons'
import { isNil } from 'lodash'

import { useReselector } from '@/utils/sharedHooks'
import {
  selectHeardProductPlanInterval,
  selectHeardProductPlanPrice,
  selectIsBasicPlan,
  StripeInterval,
} from '@/reducers/subscription.slice'
import {
  selectEligibleForPlaidStatements,
  selectFinancialAccounts,
} from '@/selectors/financeSelectors'
import {
  getAccountUploadStatuses,
  StatementStatus,
  StatementStatusType,
} from '@/actions/financialAccountActions'
import {
  CATCHUP_BOOKKEEPING_PRICE_PER_MONTH,
  fetchStripePlans,
} from '@/constants/pricingConstants'

import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { getTransactionsByType } from '@/features/transactions/transactions.selectors'

// TODO: move to shared tax utils
import { selectNumberOfQuartersWithFederalEstimate } from '@/features/taxes/quarterly/userTaxEstimates.selector'

import { filterNulls, useAppDispatch } from '@/utils/typeHelpers'
import {
  centsToCurrency,
  centsToDollars,
  formatCurrency,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { isEligibleForDiscount } from './userCancellation.selectors'
import {
  CancellationStepIdentifier,
  FaAvailableConnectionType,
  fetchTotalCompletedReconciliationCount,
  updateAutomaticCancellationAnalyticsRecord,
} from './userCancellation.slice'

import { TypographyStyles, TextColors } from '@/styles'
import { Button, Card, CardColors, Icon, Link, Text, Tooltip } from '@/ui'

export interface CancellationFlowStaticValues {
  time_saved_per_account_reconciliation_in_seconds?: number
  time_saved_per_qte_calculation_in_seconds?: number
  average_money_saved_in_cents?: number
  effective_tax_rate?: number
  average_time_saved_in_seconds?: number
  time_saved_per_transaction_categorization_in_seconds?: number
  minimum_money_threshold_in_cents?: number
  minimum_time_threshold_in_seconds?: number
  average_number_of_reconciliations_per_year?: number
  average_number_of_transactions_per_year?: number
  bookkeeping_software_monthly_fee_in_cents?: number
  done_for_you_monthly_bookkeeping_monthly_fee_in_cents?: number
  tax_advisory_with_local_cpa_hourly_fee_in_cents?: number
  annual_personal_tax_return_filing_yearly_fee_in_cents?: number
  annual_business_tax_return_filing_yearly_fee_in_cents?: number
  payroll_monthly_fee_in_cents?: number
  s_corp_setup_and_compliance_in_cents?: number
  monthly_subscription_discount_code?: string
  yearly_subscription_discount_code?: string
}

export const getDefaultPrice = (isMonthly: boolean, isScorp: boolean) => {
  const prices = fetchStripePlans()
  const planType = isScorp ? 'scorp_solo' : 'sole_prop_solo'
  const interval = isMonthly ? 'monthly' : 'annual'

  return prices[planType][interval].amountInCents
}

export const useGetSaveMoneyAndTimePageData = (recordId?: number) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [showPersonalized, setShowPersonalized] = useState<boolean>()
  const cancellationSaveFlowStaticValues: CancellationFlowStaticValues =
    useObjectFlagValue(FEATURE_FLAG_KEYS.cancellationSaveFlowStaticValues, {})

  const [saveTimeAndMoneyData, setSaveTimeAndMoneyData] = useState<{
    moneySavedInCents: number
    timeSavedInSeconds: number
    totalReconciliations: number
    totalCategorizedTransactions: number
    quartersWithEstimates: number
  }>()

  const [accountUploadStatuses, setAccountUploadStatuses] =
    useState<StatementStatus[]>()

  // For saving time and money
  const businessTransactions = useReselector(
    getTransactionsByType,
    'businessExpenses'
  )

  const allCategorizedTransactions = useReselector(
    getTransactionsByType,
    'categorized'
  )

  const quartersWithEstimates = useReselector(
    selectNumberOfQuartersWithFederalEstimate
  )

  const getSaveMoneyAndTimeData = useCallback(async () => {
    const {
      time_saved_per_account_reconciliation_in_seconds,
      time_saved_per_qte_calculation_in_seconds,
      effective_tax_rate,
      time_saved_per_transaction_categorization_in_seconds,
    } = cancellationSaveFlowStaticValues

    const totalReconciliations =
      (await fetchTotalCompletedReconciliationCount()(dispatch)) ?? 0

    const totalCategorizedTransactions = allCategorizedTransactions.length

    //if the static values aren't set, default to 0 so that we show the default
    //"average" user saves ... message
    if (
      !time_saved_per_account_reconciliation_in_seconds ||
      !time_saved_per_qte_calculation_in_seconds ||
      !effective_tax_rate ||
      !time_saved_per_transaction_categorization_in_seconds
    ) {
      return {
        moneySavedInCents: 0,
        timeSavedInSeconds: 0,
        totalReconciliations,
        totalCategorizedTransactions,
        quartersWithEstimates,
      }
    }

    const businessTransactionsTotalInCents = Math.abs(
      businessTransactions.reduce(
        (total, transaction) => total + transaction.amountInCents,
        0
      )
    )

    const moneySavedInCents = Math.ceil(
      centsToCurrency(businessTransactionsTotalInCents).multiply(
        effective_tax_rate
      ).intValue
    )

    const timeSavedInSeconds =
      totalCategorizedTransactions *
        time_saved_per_transaction_categorization_in_seconds +
      totalReconciliations * time_saved_per_account_reconciliation_in_seconds +
      quartersWithEstimates * time_saved_per_qte_calculation_in_seconds

    return {
      moneySavedInCents,
      timeSavedInSeconds,
      totalReconciliations,
      totalCategorizedTransactions,
      quartersWithEstimates,
    }
  }, [
    dispatch,
    cancellationSaveFlowStaticValues,
    businessTransactions,
    allCategorizedTransactions,
    quartersWithEstimates,
  ])

  const showPersonalizedSavingsPage = useCallback(async () => {
    const data = await getSaveMoneyAndTimeData()

    const {
      minimum_money_threshold_in_cents,
      minimum_time_threshold_in_seconds,
    } = cancellationSaveFlowStaticValues

    return (
      !isNil(minimum_money_threshold_in_cents) &&
      !isNil(minimum_time_threshold_in_seconds) &&
      data.moneySavedInCents > minimum_money_threshold_in_cents &&
      data.timeSavedInSeconds > minimum_time_threshold_in_seconds
    )
  }, [getSaveMoneyAndTimeData, cancellationSaveFlowStaticValues])

  const saveTimeAndMoneyValues = useMemo(() => {
    if (!saveTimeAndMoneyData) {
      return null
    }

    const {
      average_money_saved_in_cents,
      average_time_saved_in_seconds,
      average_number_of_reconciliations_per_year,
      average_number_of_transactions_per_year,
    } = cancellationSaveFlowStaticValues

    // if the average data isn't on the flag, we have no choice but to show their actual data
    const showPersonalizedPage =
      showPersonalized &&
      !isNil(average_money_saved_in_cents) &&
      !isNil(average_time_saved_in_seconds)

    const {
      moneySavedInCents,
      timeSavedInSeconds,
      totalReconciliations,
      totalCategorizedTransactions,
      quartersWithEstimates,
    } = saveTimeAndMoneyData

    const dollarsSaved = formatCurrency(
      Math.ceil(
        centsToDollars(
          showPersonalizedPage || !average_money_saved_in_cents
            ? moneySavedInCents
            : average_money_saved_in_cents
        )
      ),
      { hideDecimalsIfZero: true }
    )

    const timeSaved =
      showPersonalizedPage ||
      !average_money_saved_in_cents ||
      !average_time_saved_in_seconds
        ? timeSavedInSeconds
        : average_time_saved_in_seconds

    const timeString = Math.ceil(timeSaved / 3600).toLocaleString()

    const transactionsCount = showPersonalizedPage
      ? totalCategorizedTransactions
      : average_number_of_transactions_per_year

    const reconciliationsCount = showPersonalizedPage
      ? totalReconciliations
      : average_number_of_reconciliations_per_year

    return {
      dollarsSaved,
      timeString,
      transactionsCount,
      reconciliationsCount,
      quartersWithEstimates,
      showPersonalizedPage,
    }
  }, [showPersonalized, saveTimeAndMoneyData, cancellationSaveFlowStaticValues])

  //For financial account alert
  const eligibleForPlaidStatements = useReselector(
    selectEligibleForPlaidStatements
  )

  const financialAccounts = useReselector(selectFinancialAccounts)

  const faAccountAlertType = useMemo(() => {
    const lbaEligibleAccountIds = accountUploadStatuses
      ?.filter(
        (status) => status.status === StatementStatusType.LIMITED_BANK_ACCESS
      )
      .map((status) => status.id)

    const showLba = Object.values(financialAccounts).some(
      (account) =>
        lbaEligibleAccountIds?.includes(account.id) &&
        account.bankAccessEnabledAt === null
    )

    const noSupport = accountUploadStatuses?.every(
      (status) => status.status === StatementStatusType.NO_SUPPORT
    )

    if (eligibleForPlaidStatements) {
      return FaAvailableConnectionType.PLAID_STATEMENTS
    } else if (showLba) {
      return FaAvailableConnectionType.LBA
    } else if (noSupport) {
      return FaAvailableConnectionType.CREATE_NEW_ACCOUNT
    } else {
      return undefined
    }
  }, [eligibleForPlaidStatements, accountUploadStatuses, financialAccounts])

  const financialAccountAlert = useMemo(() => {
    if (!faAccountAlertType) {
      return null
    }

    let alertBody
    let alertButton

    switch (faAccountAlertType) {
      case FaAvailableConnectionType.PLAID_STATEMENTS: {
        alertBody =
          'Connect your bank through Plaid to automatically upload all your statements each month.'
        alertButton = (
          <Button
            variant="secondary"
            onClick={() => {
              localStorage.setItem('plaidStatementReloginModalShown', 'false')
              if (recordId) {
                dispatch(
                  updateAutomaticCancellationAnalyticsRecord(recordId, {
                    finalStepReached:
                      CancellationStepIdentifier.FINANCIAL_ACCOUNT_UPDATE_CLICKED,
                  })
                )
              }

              navigate('/accounts')
            }}
          >
            Connect bank
          </Button>
        )
        break
      }

      case FaAvailableConnectionType.LBA: {
        alertBody =
          'You can grant Heard limited bank access through your bank, and it will allow us to automatically upload your statements each month.'

        //update with proper link
        alertButton = (
          <Button
            variant="secondary"
            onClick={() => {
              if (recordId) {
                dispatch(
                  updateAutomaticCancellationAnalyticsRecord(recordId, {
                    finalStepReached:
                      CancellationStepIdentifier.FINANCIAL_ACCOUNT_UPDATE_CLICKED,
                  })
                )
              }
            }}
            asChild
          >
            <Link
              href="https://support.joinheard.com/hc/en-us/articles/18202072914071-What-is-limited-bank-access"
              newPage
            >
              Learn More
            </Link>
          </Button>
        )

        break
      }

      case FaAvailableConnectionType.CREATE_NEW_ACCOUNT: {
        alertBody =
          'By opening up a separate business bank account that is supported by Plaid, we can save your time by automatically uploading your statements each month.'
        alertButton = (
          <Button
            variant="secondary"
            onClick={() => {
              if (recordId) {
                dispatch(
                  updateAutomaticCancellationAnalyticsRecord(recordId, {
                    finalStepReached:
                      CancellationStepIdentifier.FINANCIAL_ACCOUNT_UPDATE_CLICKED,
                  })
                )
              }
            }}
            asChild
          >
            <Link
              newPage
              href={
                'https://support.joinheard.com/hc/en-us/articles/23569956149399-Bank-Statements-at-Heard'
              }
            >
              Learn more
            </Link>
          </Button>
        )

        break
      }

      default:
        break
    }

    return (
      <Card color={CardColors.EMPHASIS}>
        <div className="flex flex-col gap-4">
          <Text variant={TypographyStyles.H3}>
            Save even more time with auto-upload of monthly statements
          </Text>

          <Text variant={TypographyStyles.BODY_MD}>{alertBody}</Text>

          {alertButton}
        </div>
      </Card>
    )
  }, [faAccountAlertType, dispatch, recordId, navigate])

  useEffect(() => {
    const fetchData = async () => {
      const showPersonalizedVariable = await showPersonalizedSavingsPage()
      setShowPersonalized(showPersonalizedVariable)
      const saveTimeAndMoneyDataVariable = await getSaveMoneyAndTimeData()
      setSaveTimeAndMoneyData(saveTimeAndMoneyDataVariable)
      const statusesResponse = await dispatch(getAccountUploadStatuses())
      if (statusesResponse) {
        setAccountUploadStatuses(statusesResponse)
      }
    }
    fetchData()
  }, [showPersonalizedSavingsPage, getSaveMoneyAndTimeData, dispatch])

  return {
    ...saveTimeAndMoneyValues,
    faAccountAlertType,
    financialAccountAlert,
  }
}

export const useGetCostComparisonPageData = (isScorp: boolean) => {
  //should not return null, but if it does, default to monthly
  const currentSubscriptionInterval =
    useReselector(selectHeardProductPlanInterval) ?? StripeInterval.month

  const isMonthly = currentSubscriptionInterval === StripeInterval.month

  const defaultPrice = centsToCurrency(
    Number(getDefaultPrice(isMonthly, isScorp))
  )

  const currentSubscriptionPrice = formatCurrencyFromCents(
    useReselector(selectHeardProductPlanPrice) ?? defaultPrice,
    { hideDecimalsIfZero: true }
  )

  const cancellationSaveFlowStaticValues: CancellationFlowStaticValues =
    useObjectFlagValue(FEATURE_FLAG_KEYS.cancellationSaveFlowStaticValues, {})

  const isBasicPlan = useReselector(selectIsBasicPlan)

  const {
    bookkeeping_software_monthly_fee_in_cents,
    done_for_you_monthly_bookkeeping_monthly_fee_in_cents,
    annual_personal_tax_return_filing_yearly_fee_in_cents,
    annual_business_tax_return_filing_yearly_fee_in_cents,
    tax_advisory_with_local_cpa_hourly_fee_in_cents,
    s_corp_setup_and_compliance_in_cents,
    payroll_monthly_fee_in_cents,
  } = cancellationSaveFlowStaticValues

  let subText

  if (isScorp) {
    subText = 'S corporations'
  } else if (isBasicPlan) {
    subText = 'Basic Plan'
  } else {
    subText = 'Solo Annual Plan for LLC or Sole Prop'
  }

  const currentRateCard = useMemo(
    () => (
      <Card color={CardColors.EMPHASIS} className="px-0 pb-0 grow-1">
        <Text variant={TypographyStyles.H2} textAlign="center">
          Your current rate
        </Text>

        <Card color={CardColors.LEVEL_0} className="mb-0">
          <div className="flex flex-col gap-4 p-4">
            <Text variant={TypographyStyles.EYEBROW} color={TextColors.DEFAULT}>
              {isMonthly ? 'Monthly' : 'Annual'} plan
            </Text>

            <div className="min-h-fit">
              <div className="flex flex-row items-end">
                <Text
                  variant={TypographyStyles.DISPLAY_MD}
                  color={TextColors.GREEN}
                  textAlign="left"
                >
                  {currentSubscriptionPrice}
                </Text>

                {isMonthly && (
                  <Text
                    className="mb-2 text-[22px] font-normal"
                    color={TextColors.GREEN}
                  >
                    /mo
                  </Text>
                )}
              </div>

              <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
                Billed {isMonthly ? 'monthly' : 'annually'}
              </Text>
            </div>

            <Card color={CardColors.PRIORITY}>
              <div className="flex flex-col gap-4 p-4">
                <div className="flex items-center">
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    color={TextColors.DEFAULT}
                  >
                    Catch up bookkeeping
                  </Text>

                  <Tooltip>
                    <Tooltip.Trigger>
                      <Icon icon={faCircleInfo} className="ml-1" />
                    </Tooltip.Trigger>

                    <Tooltip.Content>
                      Your catch-up bookkeeping was completed as part of your
                      onboarding, and your future bookkeeping is included as
                      part of your plan.
                    </Tooltip.Content>
                  </Tooltip>
                </div>

                <div className="min-h-fit">
                  <Text variant={TypographyStyles.H1} color={TextColors.GREEN}>
                    $0
                  </Text>
                </div>

                <div className="min-h-fit">
                  <Text
                    variant={TypographyStyles.BODY_XS}
                    color={TextColors.MUTED}
                  >
                    Already completed during onboarding
                  </Text>
                </div>
              </div>
            </Card>
          </div>
        </Card>
      </Card>
    ),
    [currentSubscriptionPrice, isMonthly]
  )

  const newMemberRateCard = useMemo(
    () => (
      <Card color={CardColors.WARNING} className="px-0 pb-0 grow-1">
        <Text variant={TypographyStyles.H2} textAlign="center">
          New member rate
        </Text>

        <Card color={CardColors.LEVEL_0}>
          <div className="flex flex-col gap-4 p-4">
            <Text variant={TypographyStyles.EYEBROW} color={TextColors.DEFAULT}>
              {isMonthly ? 'Monthly' : 'Annual'} plan
            </Text>

            <div className="min-h-fit">
              <div className="flex flex-row items-end">
                <Text
                  variant={TypographyStyles.DISPLAY_MD}
                  color={TextColors.PRIORITY}
                  textAlign="left"
                >
                  {formatCurrency(defaultPrice, { hideDecimalsIfZero: true })}+
                </Text>

                {isMonthly && (
                  <Text
                    className="mb-2 text-[22px] font-normal"
                    color={TextColors.PRIORITY}
                  >
                    /mo
                  </Text>
                )}
              </div>

              <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
                Billed {isMonthly ? 'monthly' : 'annually'}, rate subject to
                change
              </Text>
            </div>

            <Card color={CardColors.PRIORITY}>
              <div className="flex flex-col gap-4 p-4">
                <div className="flex items-center">
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    color={TextColors.DEFAULT}
                  >
                    Catch up bookkeeping
                  </Text>

                  <Tooltip>
                    <Tooltip.Trigger>
                      <Icon icon={faCircleInfo} className="ml-1" />
                    </Tooltip.Trigger>

                    <Tooltip.Content>
                      To get your books up-to-date, there is a one-time catch up
                      bookkeeping fee of{' '}
                      {formatCurrency(CATCHUP_BOOKKEEPING_PRICE_PER_MONTH, {
                        hideDecimalsIfZero: true,
                      })}{' '}
                      for each month of transactions that need to be reviewed.
                    </Tooltip.Content>
                  </Tooltip>
                </div>

                <div className="min-h-fit">
                  <Text
                    variant={TypographyStyles.H1}
                    color={TextColors.PRIORITY}
                  >
                    {formatCurrency(CATCHUP_BOOKKEEPING_PRICE_PER_MONTH, {
                      hideDecimalsIfZero: true,
                    })}
                    /mo
                  </Text>
                </div>

                <div className="min-h-fit">
                  <Text
                    variant={TypographyStyles.BODY_XS}
                    color={TextColors.MUTED}
                  >
                    One-time fee for each missed month
                  </Text>
                </div>
              </div>
            </Card>
          </div>
        </Card>
      </Card>
    ),
    [defaultPrice, isMonthly]
  )

  return {
    subText,
    isMonthly,
    currentSubscriptionPrice,
    bookkeeping_software_monthly_fee_in_cents,
    done_for_you_monthly_bookkeeping_monthly_fee_in_cents,
    annual_personal_tax_return_filing_yearly_fee_in_cents,
    annual_business_tax_return_filing_yearly_fee_in_cents,
    tax_advisory_with_local_cpa_hourly_fee_in_cents,
    s_corp_setup_and_compliance_in_cents,
    payroll_monthly_fee_in_cents,
    currentRateCard,
    newMemberRateCard,
  }
}

export enum CANCELLATION_FLOW_STEP {
  savingMoneyAndTime = 'savingMoneyAndTime',
  costComparison = 'costComparison',
  discountOffer = 'discountOffer',
  discountComplete = 'discountComplete',
  cancellationComplete = 'cancellationComplete',
  cancellationPrevented = 'cancellationPrevented',
}

export const useSelfServiceCancellationSteps = (
  currentStep: CANCELLATION_FLOW_STEP
) => {
  const [_, setSearchParams] = useSearchParams()
  const location = useLocation()

  const enableDiscounts = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableCancellationDiscounts,
    false
  )

  const eligibleForDiscount = useReselector(
    isEligibleForDiscount,
    enableDiscounts
  )

  const calculateSteps = useCallback(
    ({ eligibleForDiscount }: { eligibleForDiscount: boolean }) => {
      return filterNulls([
        CANCELLATION_FLOW_STEP.savingMoneyAndTime,
        CANCELLATION_FLOW_STEP.costComparison,
        eligibleForDiscount ? CANCELLATION_FLOW_STEP.discountOffer : null,
        CANCELLATION_FLOW_STEP.cancellationComplete,
      ])
    },
    []
  )

  const goToNextStep = useCallback(
    (cancellationDetails?: {
      cancellationComment?: string
      cancellationReason?: string
    }) => {
      const steps = calculateSteps({ eligibleForDiscount })
      const currentStepIndex = steps.indexOf(currentStep)
      const nextStep = steps[currentStepIndex + 1]
      setSearchParams(
        { step: nextStep },
        {
          state: {
            recordId: location?.state?.recordId,
            cancellationDetails:
              cancellationDetails ?? location?.state?.cancellationDetails,
          },
        }
      )
    },
    [
      calculateSteps,
      currentStep,
      setSearchParams,
      location,
      eligibleForDiscount,
    ]
  )

  return goToNextStep
}
